import { useRef, useState } from 'react';
import { useAuth } from '@/lib/auth/AuthContext';
import { useUploadFile, validateAudioFile, validateImageFile, type AssetType } from '@/admin/hooks/useUpload';

interface FileUploadFieldProps {
  label: string;
  topicSlug: string;
  assetType: AssetType;
  currentPath?: string | null;
  onUploadComplete: (s3Key: string) => void;
  onClear: () => void;
  accept: string;
  helpText?: string;
}

export function FileUploadField({
  label,
  topicSlug,
  assetType,
  currentPath,
  onUploadComplete,
  onClear,
  accept,
  helpText,
}: FileUploadFieldProps) {
  const { accessToken } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const uploadFile = useUploadFile(accessToken ?? '');

  const isAudio = assetType === 'INTRO' || assetType === 'OUTRO';

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    const validationError = isAudio
      ? validateAudioFile(file)
      : validateImageFile(file);

    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setUploadProgress(0);

    try {
      const result = await uploadFile.mutateAsync({
        file,
        options: {
          topicSlug,
          assetType,
          onProgress: (progress) => setUploadProgress(progress.percentage),
        },
      });

      onUploadComplete(result.s3Key);
      setUploadProgress(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      setUploadProgress(null);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClear = () => {
    onClear();
    setError(null);
    setUploadProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isUploading = uploadProgress !== null;

  const inputId = `file-upload-${assetType}`;

  return (
    <div className="space-y-2">
      <label htmlFor={inputId} className="label">{label}</label>

      {currentPath ? (
        <div className="flex items-center gap-2 rounded-xl border border-gray-700 bg-gray-800/50 p-3">
          <div className="flex-1 truncate text-sm text-gray-300" title={currentPath}>
            {currentPath.split('/').pop()}
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-red-500/20 hover:text-red-400"
            title="Remove file"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" role="img" aria-labelledby="remove-icon-title">
              <title id="remove-icon-title">Remove file</title>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : isUploading ? (
        <div className="rounded-xl border border-gray-700 bg-gray-800/50 p-3">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="mb-1 flex justify-between text-sm">
                <span className="text-gray-300">Uploading...</span>
                <span className="text-indigo-400">{uploadProgress}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-700">
                <div
                  className="h-full rounded-full bg-indigo-500 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative">
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            className="hidden"
            id={inputId}
            disabled={!topicSlug}
          />
          <label
            htmlFor={inputId}
            className={`flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-700 bg-gray-800/30 px-4 py-4 text-gray-400 transition-colors hover:border-indigo-500/50 hover:bg-gray-800/50 hover:text-gray-300 ${
              !topicSlug ? 'cursor-not-allowed opacity-50' : ''
            }`}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <span className="text-sm">
              {topicSlug ? 'Click to upload' : 'Save topic first to enable upload'}
            </span>
          </label>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-400">{error}</p>
      )}

      {helpText && !error && (
        <p className="text-xs text-gray-500">{helpText}</p>
      )}
    </div>
  );
}
